import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import feedbackApi from "../api/feedbackApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import performanceRoutes from "../routes/performanceRoutes";

const FeedbackDetailPage = () => {
  const navigate = useNavigate();
  const { feedbackId } = useParams();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const tileBg = useColorModeValue("gray.50", "gray.700");

  const sentimentColorScheme = {
    POSITIVE: "green",
    NEUTRAL: "gray",
    IMPROVEMENT: "orange",
  };

  const visibilityColorScheme = {
    PRIVATE: "purple",
    MANAGER: "blue",
    EMPLOYEE: "teal",
    HR: "pink",
  };

  const canReadFeedback = can(PERFORMANCE_PERMISSIONS.FEEDBACK_READ);

  if (!canReadFeedback) {
    return (
      <ModulePageShell
        title="Feedback Detail"
        description="View feedback details, sender, receiver, and message content."
      >
        <EmptyState message="You do not have permission to view feedback details." />
      </ModulePageShell>
    );
  }

  const feedbackQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.feedback, "detail", feedbackId],
    queryFn: () => feedbackApi.getFeedbackById(feedbackId),
    enabled: Boolean(feedbackId),
  });

  const feedback = feedbackQuery.data?.data;
  const acknowledged = Boolean(feedback?.acknowledgement?.acknowledged);

  return (
    <ModulePageShell
      title="Feedback Detail"
      description="View feedback details, sender, receiver, and message content."
      onRefresh={() => feedbackQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(performanceRoutes.feedback)}
        >
          Back to Feedback
        </Button>
      }
    >
      {feedbackQuery.isLoading ? (
        <LoadingState />
      ) : !feedback ? (
        <EmptyState message="Feedback record not found." />
      ) : (
        <VStack align="stretch" spacing={5}>
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={{ base: 4, md: 6 }}
          >
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" align="flex-start" wrap="wrap">
                <VStack align="stretch" spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="short">
                    {feedback.title || "Feedback"}
                  </Text>
                  <Text fontSize="sm" color={mutedText}>
                    Created{" "}
                    {feedback.createdAt
                      ? new Date(feedback.createdAt).toLocaleString()
                      : "-"}
                  </Text>
                </VStack>
                <Badge
                  colorScheme={acknowledged ? "green" : "yellow"}
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {acknowledged ? "ACKNOWLEDGED" : "NEW"}
                </Badge>
              </HStack>

              <HStack wrap="wrap" spacing={2}>
                <Badge
                  colorScheme={
                    sentimentColorScheme[feedback.sentiment] || "gray"
                  }
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {feedback.sentiment || "-"}
                </Badge>
                <Badge
                  colorScheme={
                    visibilityColorScheme[feedback.visibility] || "blue"
                  }
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {feedback.visibility || "-"}
                </Badge>
                <Badge
                  colorScheme={feedback.isAnonymous ? "purple" : "gray"}
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {feedback.isAnonymous ? "Anonymous" : "Named"}
                </Badge>
              </HStack>

              <Divider />

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <Box bg={tileBg} borderRadius="md" p={3}>
                  <Text
                    fontSize="xs"
                    color={mutedText}
                    textTransform="uppercase"
                  >
                    From
                  </Text>
                  <Text fontWeight="semibold">
                    {feedback.fromEmployeeId?.name || "-"}
                  </Text>
                  <Text fontSize="xs" color={mutedText}>
                    {feedback.fromEmployeeId?.employeeId ||
                      feedback.fromEmployeeId?.email ||
                      "-"}
                  </Text>
                </Box>

                <Box bg={tileBg} borderRadius="md" p={3}>
                  <Text
                    fontSize="xs"
                    color={mutedText}
                    textTransform="uppercase"
                  >
                    To
                  </Text>
                  <Text fontWeight="semibold">
                    {feedback.toEmployeeId?.name || "-"}
                  </Text>
                  <Text fontSize="xs" color={mutedText}>
                    {feedback.toEmployeeId?.employeeId ||
                      feedback.toEmployeeId?.email ||
                      "-"}
                  </Text>
                </Box>

                <Box bg={tileBg} borderRadius="md" p={3}>
                  <Text
                    fontSize="xs"
                    color={mutedText}
                    textTransform="uppercase"
                  >
                    Feedback Type
                  </Text>
                  <Text fontWeight="semibold">
                    {feedback.feedbackTypeId?.name || "-"}
                  </Text>
                  <Text fontSize="xs" color={mutedText}>
                    {feedback.feedbackTypeId?.code || "-"}
                  </Text>
                </Box>
              </SimpleGrid>

              <HStack wrap="wrap" spacing={4}>
                <Text fontSize="sm">
                  Acknowledged At:{" "}
                  {feedback.acknowledgement?.acknowledgedAt
                    ? new Date(
                        feedback.acknowledgement.acknowledgedAt,
                      ).toLocaleString()
                    : "-"}
                </Text>
                <Text fontSize="sm">
                  Type Visibility: {feedback.feedbackTypeId?.visibility || "-"}
                </Text>
              </HStack>
            </VStack>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={{ base: 4, md: 6 }}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontSize="lg" fontWeight="semibold">
                Message
              </Text>
              <Text fontSize="sm" color={mutedText}>
                Full feedback content as submitted by the sender.
              </Text>
              <Box
                className="wysiwyg-editor"
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
                p={4}
                sx={{
                  ".ProseMirror": {
                    padding: 0,
                    minHeight: "auto",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: feedback.message || "<p>-</p>",
                }}
              />
            </VStack>
          </Box>
        </VStack>
      )}
    </ModulePageShell>
  );
};

export default FeedbackDetailPage;
